import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Bell, Save, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const AlertThresholds = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [thresholds, setThresholds] = useState({
    revenue_drop_percentage: 20,
    mrr_drop_percentage: 15,
    user_growth_min_daily: 5,
    patient_growth_min_daily: 10,
    conversion_rate_min: 30,
    delivery_rate_min_email: 90,
    delivery_rate_min_sms: 85
  });

  useEffect(() => {
    fetchThresholds();
  }, []);

  const fetchThresholds = async () => {
    try {
      const response = await api.get('/platform/alert-thresholds');
      setThresholds(response.data);
    } catch (error) {
      logger.error('Failed to load thresholds:', error);
      toast.error('Failed to load alert thresholds');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/platform/alert-thresholds', thresholds);
      toast.success('Alert thresholds saved successfully');
    } catch (error) {
      logger.error('Failed to save thresholds:', error);
      toast.error('Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  const checkAlerts = async () => {
    setChecking(true);
    try {
      const response = await api.post('/platform/check-alerts');
      setAlerts(response.data.alerts || []);
      if (response.data.alerts.length === 0) {
        toast.success('All metrics within thresholds!');
      } else {
        toast.warning(`Found ${response.data.alerts.length} alert(s)`);
      }
    } catch (error) {
      logger.error('Failed to check alerts:', error);
      toast.error('Failed to check alerts');
    } finally {
      setChecking(false);
    }
  };

  const handleChange = (field, value) => {
    setThresholds(prev => ({ ...prev, [field]: Number(value) }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'warning':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading thresholds...</div>
      </div>
    );
  }

  return (
    <div data-testid="alert-thresholds-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/platform/analytics')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Alert Thresholds</h1>
            <p className="text-slate-500">Configure metric thresholds for automated alerts</p>
          </div>
          <Button
            onClick={checkAlerts}
            disabled={checking}
            variant="outline"
            size="sm"
          >
            {checking ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Checking...</>
            ) : (
              <><Bell className="w-4 h-4 mr-2" />Check Now</>
            )}
          </Button>
        </div>
      </header>

      <div className="page-content max-w-4xl">
        <div className="space-y-6">
          {/* Active Alerts */}
          {alerts.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
                  <AlertTriangle className="w-5 h-5" />
                  Active Alerts ({alerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {alerts.map((alert, index) => (
                  <div key={`${alert.type || alert.metric}-${alert.message}`} className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium mb-1">{alert.metric}</p>
                        <p className="text-sm">{alert.message}</p>
                      </div>
                      <Badge variant="outline" className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span>Current: <strong>{alert.current}</strong></span>
                      <span>Threshold: <strong>{alert.threshold}</strong></span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Revenue Thresholds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Revenue & Financial Thresholds</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="revenue_drop">Revenue Drop Alert (%)</Label>
                <Input
                  id="revenue_drop"
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.revenue_drop_percentage}
                  onChange={(e) => handleChange('revenue_drop_percentage', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Alert when revenue drops by this percentage</p>
              </div>

              <div>
                <Label htmlFor="mrr_drop">MRR Drop Alert (%)</Label>
                <Input
                  id="mrr_drop"
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.mrr_drop_percentage}
                  onChange={(e) => handleChange('mrr_drop_percentage', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Alert when MRR drops by this percentage</p>
              </div>
            </CardContent>
          </Card>

          {/* Growth Thresholds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Growth Thresholds</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user_growth">Minimum Daily User Growth</Label>
                <Input
                  id="user_growth"
                  type="number"
                  min="0"
                  value={thresholds.user_growth_min_daily}
                  onChange={(e) => handleChange('user_growth_min_daily', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Alert if average daily users below this</p>
              </div>

              <div>
                <Label htmlFor="patient_growth">Minimum Daily Patient Growth</Label>
                <Input
                  id="patient_growth"
                  type="number"
                  min="0"
                  value={thresholds.patient_growth_min_daily}
                  onChange={(e) => handleChange('patient_growth_min_daily', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Alert if average daily patients below this</p>
              </div>
            </CardContent>
          </Card>

          {/* Conversion & Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Conversion & Performance</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="conversion_rate">Minimum Conversion Rate (%)</Label>
                <Input
                  id="conversion_rate"
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.conversion_rate_min}
                  onChange={(e) => handleChange('conversion_rate_min', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Trial to paid conversion rate</p>
              </div>

              <div>
                <Label htmlFor="email_delivery">Min Email Delivery Rate (%)</Label>
                <Input
                  id="email_delivery"
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.delivery_rate_min_email}
                  onChange={(e) => handleChange('delivery_rate_min_email', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Email delivery success rate</p>
              </div>

              <div>
                <Label htmlFor="sms_delivery">Min SMS Delivery Rate (%)</Label>
                <Input
                  id="sms_delivery"
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.delivery_rate_min_sms}
                  onChange={(e) => handleChange('delivery_rate_min_sms', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">SMS delivery success rate</p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/platform/analytics')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-red-500 hover:bg-red-600"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Thresholds'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertThresholds;
